import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { BulkActionsBar, BulkCheckbox } from "@/components/admin/BulkActionsBar";
import NotifyContactsModal from "@/components/admin/NotifyContactsModal";
import { Plus, X, Edit3, Trash2, Check, X as XIcon, ExternalLink, Clock, CheckCircle2, XCircle, Wine, Search, Percent, Ticket, Gift, Sparkles } from "lucide-react";

const TYPES = [
  { id: "discount", label: "Réduction", icon: Percent },
  { id: "promo_code", label: "Code promo", icon: Ticket },
  { id: "free_trial", label: "Essai gratuit", icon: Gift },
  { id: "perk", label: "Avantage", icon: Sparkles },
];

const STATUSES = [
  { id: "all", label: "Toutes" },
  { id: "pending", label: "En attente" },
  { id: "published", label: "Publiées" },
  { id: "rejected", label: "Rejetées" },
];

function emptyForm() {
  return { title: "", description: "", type: "discount", value: "", promo_code: "", url: "", conditions: "", expires_at: "", cover_image: "", is_public: false };
}

function StatusBadge({ status }) {
  const m = { pending: { l: "En attente", I: Clock, c: "bg-amber-50 text-amber-700" }, published: { l: "Publiée", I: CheckCircle2, c: "bg-emerald-50 text-emerald-700" }, rejected: { l: "Rejetée", I: XCircle, c: "bg-rose-50 text-rose-700" } }[status] || { l: status, I: Clock, c: "bg-neutral-100 text-neutral-600" };
  const I = m.I;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${m.c}`}><I className="h-2.5 w-2.5" />{m.l}</span>;
}

export default function AdminClub() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [selected, setSelected] = useState(new Set());
  const [notifyOpen, setNotifyOpen] = useState(false);

  const toggleSel = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };
  const clearSel = () => setSelected(new Set());

  const load = useCallback(() => {
    const url = filter === "all" ? "/admin/club" : `/admin/club?status=${filter}`;
    api.get(url).then((r) => setItems(r.data || [])).catch(() => toast.error("Erreur de chargement"));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (o) => { setEdit(o); setForm({ ...emptyForm(), ...o }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (edit) {
        await api.patch(`/admin/club/${edit.id}`, form);
        toast.success("Offre mise à jour");
      } else {
        await api.post("/admin/club", form);
        toast.success("Offre créée et publiée");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const approve = async (o) => {
    try { await api.post(`/admin/club/${o.id}/approve`); toast.success(`✅ Offre publiée — +5 pts pour le membre`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const reject = async (o) => {
    const reason = window.prompt(`Raison du rejet pour "${o.title}" ?`, "");
    if (reason === null) return;
    try { await api.post(`/admin/club/${o.id}/reject`, { reason }); toast.success("Offre rejetée — email envoyé au membre"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Supprimer "${o.title}" ?`)) return;
    try { await api.delete(`/admin/club/${o.id}`); toast.success("Supprimée"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const filtered = items.filter((o) => !search || o.title?.toLowerCase().includes(search.toLowerCase()) || o.description?.toLowerCase().includes(search.toLowerCase()));
  const pendingCount = items.filter((o) => o.status === "pending").length;
  const selectAll = () => setSelected(new Set(filtered.map((i) => i.id)));
  const bulkDelete = async () => {
    if (!window.confirm(`Supprimer ${selected.size} offre${selected.size > 1 ? "s" : ""} ?`)) return;
    for (const id of selected) {
      try { await api.delete(`/admin/club/${id}`); } catch (e) { /* skip individual errors */ }
    }
    toast.success("Offres supprimées");
    clearSel();
    load();
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl"><Wine className="mr-2 inline h-7 w-7 text-wine-500" />Le Club</h1>
          <p className="mt-1 text-neutral-600">Offres exclusives proposées par les membres. {pendingCount > 0 && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-800" data-testid="club-pending-count">⏳ {pendingCount} en attente de validation</span>}</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="admin-club-new-btn">
          <Plus className="h-4 w-4" />Nouvelle offre
        </button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex gap-1 rounded-full bg-neutral-100 p-1">
          {STATUSES.map((s) => {
            const active = filter === s.id;
            const count = s.id === "all" ? items.length : items.filter((r) => r.status === s.id).length;
            return (
              <button key={s.id} onClick={() => setFilter(s.id)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${active ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid={`club-filter-${s.id}`}>
                {s.label} ({count})
              </button>
            );
          })}
        </div>
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-neutral-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="rounded-full border border-neutral-200 bg-white pl-8 pr-3 py-2 text-sm" />
        </div>
      </div>

      {selected.size > 0 && (
        <div className="mt-3">
          <BulkActionsBar count={selected.size} total={filtered.length} onClear={clearSel} onSelectAll={selectAll} onDelete={bulkDelete} label="offre" testId="admin-club-bulk" extraActions={
            <button onClick={() => setNotifyOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-700" data-testid="club-bulk-notify">
              🔔 Notifier des contacts
            </button>
          } />
        </div>
      )}

      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-3 py-3 w-8"><BulkCheckbox checked={filtered.length > 0 && selected.size === filtered.length} onChange={(v) => v ? selectAll() : clearSel()} testId="club-select-all" /></th>
              <th className="px-4 py-3 text-left">Offre</th>
              <th className="px-4 py-3 text-left">Type / Valeur</th>
              <th className="px-4 py-3 text-left">Soumise par</th>
              <th className="px-4 py-3 text-left">Statut</th>
              <th className="px-4 py-3 text-left">Visibilité</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-500">Aucune offre</td></tr>}
            {filtered.map((o) => {
              const T = TYPES.find((t) => t.id === o.type) || TYPES[0];
              const I = T.icon;
              return (
                <tr key={o.id} className={`border-t border-neutral-100 ${selected.has(o.id) ? "bg-rose-50/40" : ""}`} data-testid={`admin-club-row-${o.id}`}>
                  <td className="px-3 py-3"><BulkCheckbox checked={selected.has(o.id)} onChange={() => toggleSel(o.id)} testId={`club-select-${o.id}`} /></td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-neutral-900">{o.title}</div>
                    <div className="line-clamp-1 max-w-md text-xs text-neutral-500">{o.description}</div>
                    {o.promo_code && <div className="mt-1 inline-block rounded-full border border-dashed border-wine-300 bg-wine-50 px-2 py-0.5 font-mono text-[10px] font-bold text-wine-700">{o.promo_code}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-600">
                    <div className="inline-flex items-center gap-1"><I className="h-3 w-3" />{T.label}</div>
                    {o.value && <div className="font-bold text-wine-700">{o.value}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-600">
                    {o.company ? <><strong>{o.company.name}</strong><br/></> : null}
                    <span className="text-neutral-500">{o.submitted_by_name || o.submitted_by_email || "Admin"}</span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-xs">
                    {o.is_public ? <span className="rounded-full bg-blue-50 px-2 py-0.5 font-bold text-blue-700">Public</span> : <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-neutral-600">Membres</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {o.status === "pending" && (
                        <>
                          <button onClick={() => approve(o)} className="grid h-7 w-7 place-items-center rounded-full text-emerald-600 hover:bg-emerald-50" title="Approuver" data-testid={`club-approve-${o.id}`}><Check className="h-3.5 w-3.5" /></button>
                          <button onClick={() => reject(o)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Rejeter" data-testid={`club-reject-${o.id}`}><XIcon className="h-3.5 w-3.5" /></button>
                        </>
                      )}
                      {o.url && <a href={o.url} target="_blank" rel="noreferrer" className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100"><ExternalLink className="h-3.5 w-3.5" /></a>}
                      <button onClick={() => openEdit(o)} className="grid h-7 w-7 place-items-center rounded-full text-blue-600 hover:bg-blue-50" title="Modifier" data-testid={`club-edit-${o.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                      <button onClick={() => remove(o)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer" data-testid={`club-del-${o.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-club-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-navy">{edit ? <><Edit3 className="mr-1 inline h-5 w-5" /> Modifier l&apos;offre</> : <><Plus className="mr-1 inline h-5 w-5" /> Nouvelle offre</>}</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-club-title" />
              <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-club-description" />
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-club-type">
                {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="Valeur (ex: -30%)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-club-value" />
              <input value={form.promo_code} onChange={(e) => setForm({ ...form, promo_code: e.target.value })} placeholder="Code promo" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" data-testid="admin-club-code" />
              <input type="date" value={form.expires_at?.split("T")[0] || ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-club-expires" />
              <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="URL pour bénéficier" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-club-url" />
              <textarea rows={2} value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} placeholder="Conditions / mentions légales" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-club-conditions" />
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Image de couverture</label>
                <ImageUpload value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} label="" testId="admin-club-cover" />
              </div>
              <label className="md:col-span-2 inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50/50 px-3 py-2 text-xs text-neutral-700">
                <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="accent-wine-500" data-testid="admin-club-is-public" />
                <span><strong>Offre publique</strong> — visible aussi des visiteurs non connectés (lead magnet).</span>
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="admin-club-save">
                {edit ? "Mettre à jour" : "Publier"}
              </button>
            </div>
          </form>
        </div>
      )}

      {notifyOpen && (
        <NotifyContactsModal
          open={notifyOpen}
          onClose={() => { setNotifyOpen(false); clearSel(); }}
          entityType="club_offer"
          entityIds={Array.from(selected)}
          endpoint="/admin/club/notify-contacts-bulk"
          payloadKey="offer_ids"
        />
      )}
    </PortalLayout>
  );
}
